import React, { useState, useEffect, useRef, useCallback } from 'react';
import { API_DOMAIN, getHeaders } from '../Config/apiConfig';
import * as listCache from '../../../services/listCache';

const MIN_CHARS_DEFAULT = 2;
const DEBOUNCE_MS = 350;
const PAGE_SIZE_DEFAULT = 20;

function extractResults(raw) {
  if (!raw) return { results: [], next: null };
  if (Array.isArray(raw)) return { results: raw, next: null };
  if (Array.isArray(raw.results)) return { results: raw.results, next: raw.next || null };
  if (raw.data) {
    if (Array.isArray(raw.data)) return { results: raw.data, next: null };
    if (Array.isArray(raw.data.results)) return { results: raw.data.results, next: raw.data.next || null };
  }
  return { results: [], next: null };
}

/**
 * Backend-searched, debounced, cached single-select for large datasets
 * (Users, Contacts, Leads, Opportunities, Customers, Quotes, Products).
 * Single-value only — do not use for a ManyToMany/array field, use
 * MultiSelectComponent for those.
 *
 * Controlled: `value` is the selected record's id (or ''); `onChange(id)` reports selection.
 *
 * Full pipeline (see docs/ASYNC_SELECT.md for the complete write-up, including the
 * backend prefix-Trie this talks to):
 *   1. On mount, fetch page 1 of `endpoint` (never the full dataset) and cache it.
 *   2. On each keystroke, filter that cached page first (per-word prefix match) and
 *      answer instantly on any match — zero network calls.
 *   3. Only on a local miss, debounce (350ms) and hit the backend's `?search=` param,
 *      itself cached by URL for the rest of the session.
 *
 * Required: label, value, onChange, endpoint (relative API_ROUTES path), getOptionLabel(record).
 * Optional: getOptionValue (default record.id), initialLabel (known display text for `value`
 * on mount, e.g. from the record already loaded by the parent — avoids an extra fetch),
 * searchParam ('search'), minChars (2), pageSize (20), placeholder, required, disabled.
 */
const AsyncSelect = ({
  label,
  value,
  onChange,
  endpoint,
  getOptionLabel,
  getOptionValue = (o) => o.id,
  initialLabel,
  searchParam = 'search',
  minChars = MIN_CHARS_DEFAULT,
  pageSize = PAGE_SIZE_DEFAULT,
  placeholder,
  required,
  disabled,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [resolvedLabel, setResolvedLabel] = useState(initialLabel || '');
  const [options, setOptions] = useState([]);
  const [page1Options, setPage1Options] = useState([]);
  const [loading, setLoading] = useState(false);
  const [nextUrl, setNextUrl] = useState(null);
  const [highlighted, setHighlighted] = useState(-1);

  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);
  const listboxId = useRef(`asyncselect-listbox-${Math.random().toString(36).slice(2)}`).current;

  // Resolve a display label for a pre-existing value (edit mode) without a full list fetch.
  useEffect(() => {
    if (!value) { setResolvedLabel(''); return; }
    if (initialLabel) { setResolvedLabel(initialLabel); return; }
    if (resolvedLabel) return; // already have one (e.g. from a prior selection this session)
    let cancelled = false;
    const url = `${API_DOMAIN}${endpoint}${value}/`;
    listCache.fetchDeduped(url, getHeaders())
      .then((raw) => {
        if (cancelled) return;
        const record = raw?.data ?? raw;
        if (record) setResolvedLabel(getOptionLabel(record));
      })
      .catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, initialLabel, endpoint]);

  // Keep the input's idle text in sync with the resolved label when not actively editing.
  useEffect(() => {
    if (!open) setQuery(resolvedLabel || '');
  }, [resolvedLabel, open]);

  // Preload + cache page 1 on mount — this is the only unconditional fetch;
  // the full dataset is never pulled, just this one page, up front, so the
  // very first keystroke can often be answered from memory with no network call.
  useEffect(() => {
    const url = `${API_DOMAIN}${endpoint}?page_size=${pageSize}`;
    const cached = listCache.get(url);
    if (cached) {
      setPage1Options(extractResults(cached.data).results);
      if (!cached.isStale) return;
    }
    listCache.fetchDeduped(url, getHeaders())
      .then((raw) => {
        listCache.set(url, raw);
        setPage1Options(extractResults(raw).results);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpoint, pageSize]);

  // Prefix-match page 1's cached rows against a search term, per whitespace
  // token — mirrors the backend Trie's per-word-prefix semantics so results
  // are consistent whether they're answered locally or from the server.
  const searchLocal = useCallback((term) => {
    const tokens = term.toLowerCase().split(/\s+/).filter(Boolean);
    if (!tokens.length) return [];
    return page1Options.filter((opt) => {
      const label = (getOptionLabel(opt) || '').toLowerCase();
      const words = label.split(/\s+/);
      return tokens.every((tok) => words.some((w) => w.startsWith(tok)));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page1Options]);

  const runSearch = useCallback((term, url) => {
    setLoading(true);
    listCache.fetchDeduped(url, getHeaders())
      .then((raw) => {
        const { results, next } = extractResults(raw);
        listCache.set(url, raw);
        setOptions(results);
        setNextUrl(next);
        setHighlighted(results.length ? 0 : -1);
      })
      .catch(() => { setOptions([]); setNextUrl(null); })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const term = query.trim();
    if (term.length < minChars) {
      setOptions([]);
      setNextUrl(null);
      setLoading(false);
      return;
    }

    // 1. Search the cached page-1 results first — instant, no network call.
    const local = searchLocal(term);
    if (local.length > 0) {
      setOptions(local);
      setNextUrl(null);
      setHighlighted(0);
      setLoading(false);
      return;
    }

    // 2. No local match — fall back to a debounced backend search.
    debounceRef.current = setTimeout(() => {
      const url = `${API_DOMAIN}${endpoint}?${searchParam}=${encodeURIComponent(term)}&page_size=${pageSize}`;
      const cached = listCache.get(url);
      if (cached && !cached.isStale) {
        const { results, next } = extractResults(cached.data);
        setOptions(results);
        setNextUrl(next);
        setHighlighted(results.length ? 0 : -1);
      } else {
        runSearch(term, url);
      }
    }, DEBOUNCE_MS);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [query, open, endpoint, searchParam, pageSize, minChars, runSearch, searchLocal]);

  useEffect(() => {
    if (!open) return;
    const handleOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [open]);

  const selectOption = (opt) => {
    const val = getOptionValue(opt);
    const lbl = getOptionLabel(opt);
    onChange(val);
    setResolvedLabel(lbl);
    setQuery(lbl);
    setOpen(false);
    setHighlighted(-1);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange('');
    setResolvedLabel('');
    setQuery('');
    setOptions([]);
    inputRef.current?.focus();
  };

  const loadMore = () => {
    if (!nextUrl) return;
    setLoading(true);
    listCache.fetchDeduped(nextUrl, getHeaders())
      .then((raw) => {
        const { results, next } = extractResults(raw);
        setOptions((prev) => [...prev, ...results]);
        setNextUrl(next);
      })
      .finally(() => setLoading(false));
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!open) { setOpen(true); return; }
      setHighlighted((i) => Math.min(i + 1, options.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlighted((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      if (open && highlighted >= 0 && options[highlighted]) {
        e.preventDefault();
        selectOption(options[highlighted]);
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
      inputRef.current?.blur();
    }
  };

  const term = query.trim();
  const belowMinChars = term.length > 0 && term.length < minChars;
  const showNoResults = open && !loading && term.length >= minChars && options.length === 0;
  const activeOptionId = highlighted >= 0 && options[highlighted]
    ? `${listboxId}-opt-${getOptionValue(options[highlighted])}`
    : undefined;

  return (
    <div className="form-multiselect-wrap form-asyncselect-wrap" ref={containerRef}>
      <label className="form-label">
        {label}
        {required && <span className="req">*</span>}
      </label>

      <div className={`form-asyncselect-input-wrap${open ? ' is-open' : ''}`}>
        <input
          ref={inputRef}
          type="text"
          className="form-input form-asyncselect-input"
          value={query}
          disabled={disabled}
          placeholder={placeholder || `Type to search ${label.toLowerCase()}…`}
          onFocus={() => setOpen(true)}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onKeyDown={handleKeyDown}
          role="combobox"
          aria-expanded={open}
          aria-haspopup="listbox"
          aria-autocomplete="list"
          aria-controls={listboxId}
          aria-activedescendant={activeOptionId}
        />
        {loading && <span className="form-asyncselect-spinner spinner" aria-hidden="true" />}
        {!loading && value && !disabled && (
          <button
            type="button"
            className="form-asyncselect-clear"
            onClick={handleClear}
            aria-label={`Clear ${label}`}
          >
            ×
          </button>
        )}
      </div>

      {open && (
        <div className="form-multiselect-dropdown" role="listbox" id={listboxId}>
          {belowMinChars && (
            <div className="form-multiselect-empty">Type at least {minChars} characters…</div>
          )}
          {!belowMinChars && term.length === 0 && (
            <div className="form-multiselect-empty">Start typing to search…</div>
          )}
          {showNoResults && (
            <div className="form-multiselect-empty">No results for "{term}"</div>
          )}
          {options.map((opt, i) => {
            const val = getOptionValue(opt);
            return (
              <div
                key={val}
                id={`${listboxId}-opt-${val}`}
                role="option"
                aria-selected={i === highlighted}
                className={`form-multiselect-option${i === highlighted ? ' is-selected' : ''}`}
                onMouseEnter={() => setHighlighted(i)}
                onClick={() => selectOption(opt)}
              >
                {getOptionLabel(opt)}
              </div>
            );
          })}
          {nextUrl && !loading && (
            <div
              className="form-multiselect-option form-asyncselect-loadmore"
              onClick={loadMore}
            >
              Load more results…
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AsyncSelect;
